import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './Toast.module.scss';

const ToastContext = createContext(null);

const DEFAULT_DURATION = 2500;

let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message, { variant = 'default', duration = DEFAULT_DURATION } = {}) => {
      if (!message) return null;
      // eslint-disable-next-line no-plusplus
      const id = ++nextId;
      setToasts((prev) => [...prev, { id, message, variant }]);

      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const value = { showToast, dismiss };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className={styles.toastRoot} aria-live="polite" aria-atomic="true">
            <AnimatePresence initial={false}>
              {toasts.map((toast) => (
                <motion.div
                  key={toast.id}
                  layout
                  initial={{ opacity: 0, y: 12, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 12, scale: 0.96 }}
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  className={`${styles.toast} ${
                    toast.variant === 'success'
                      ? styles.success
                      : toast.variant === 'error'
                        ? styles.error
                        : ''
                  }`}
                  role={toast.variant === 'error' ? 'alert' : 'status'}
                  onClick={() => dismiss(toast.id)}
                >
                  {toast.message}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe no-op when provider is missing so callers don't have to null-check.
    return { showToast: () => null, dismiss: () => {} };
  }
  return ctx;
};

export default ToastContext;
